import { useSelector } from "react-redux";
import { useMemo } from "react";

// Workspaces where ALL markets use foods/food
const FOOD_WORKSPACES = ["pepsi_uk"];

// Per-market overrides INSIDE an otherwise-drinks workspace.
// Example: { "pepsi_global": ["UK"] }
const FOOD_WORKSPACE_MARKETS = {};

const isFoodContext = (id_client, country) => {
  if (!id_client) return false;
  if (FOOD_WORKSPACES.includes(id_client)) return true;
  const markets = FOOD_WORKSPACE_MARKETS[id_client];
  return !!(markets && country && markets.includes(country));
};

/**
 * Replace "drink/drinks" with the workspace-appropriate term in a string.
 * Uses word-boundary regex to avoid partial matches (e.g. "drinkware").
 */
const buildReplaceTerm = (isFood) => {
  if (!isFood) return (str) => str;
  return (str) => {
    if (!str) return str;
    return str
      .replace(/\bDRINKS\b/g, "FOODS")
      .replace(/\bDRINK\b/g, "FOOD")
      .replace(/\bDrinks\b/g, "Foods")
      .replace(/\bDrink\b/g, "Food")
      .replace(/\bdrinks\b/g, "foods")
      .replace(/\bdrink\b/g, "food");
  };
};

/**
 * Pure utility (non-hook) for use outside React components.
 * Pass id_client and (optionally) country explicitly.
 */
export const getProductTerms = (id_client, country) => {
  const isFood = isFoodContext(id_client, country);
  return {
    singular: isFood ? "food" : "drink",
    plural: isFood ? "foods" : "drinks",
    Singular: isFood ? "Food" : "Drink",
    Plural: isFood ? "Foods" : "Drinks",
    replaceTerm: buildReplaceTerm(isFood),
  };
};

/**
 * React hook — returns workspace-appropriate product terminology.
 * pepsi_uk -> food/foods; per-market overrides via FOOD_WORKSPACE_MARKETS.
 *
 * replaceTerm is memoized so it can be safely used in useEffect/useMemo deps.
 */
export const useProductTerm = () => {
  const id_client = useSelector((state) => state.auth.id_client);
  const country = useSelector((state) => state.filters.currentCountry);
  return useMemo(() => getProductTerms(id_client, country), [id_client, country]);
};
